import { useEffect } from 'react';

import { Card, CardHeader, CardContent, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { userInfoAtom } from '~atoms';
import { useAuthorize } from '~features/authentication';

import { useStyles } from './style';
import {
	FormField,
	SearchAutocomplete,
	SelectedOrganisationCard,
	Tip,
} from '../../../../../../components';
import {
	useWizardFormField,
	useDebouncedWizardSave,
	useDebouncedBackendFieldCheck,
} from '../../../../../../shared/hooks';
import { isEmptyObject, isFullString, isObject } from '../../../../../../shared/utility';
import * as actions from '../../../../../../store/actions';

interface AboutPolicyProps {
	className?: string;
	save(...args: unknown[]): unknown;
	name?: string;
	selectedOrganisation?: object;
	organisationsList?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
	onFetchOrganisations?(...args: unknown[]): unknown;
	currentUser?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
	onValidatePolicyName?(...args: unknown[]): unknown;
	validatedPolicyName?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
	editing?: boolean;
	currentPolicyName?: string;
}

const AboutPolicy = (props: AboutPolicyProps) => {
	const {
		className,
		save,

		organisationsList,
		onFetchOrganisations,
		name: savedName,
		selectedOrganisation: savedOrganisation,
		currentUser,
		onValidatePolicyName,
		validatedPolicyName,
		editing,

		currentPolicyName,
	} = props;
	const { t } = useTranslation();
	const { isSuperAdmin } = useAuthorize();
	const userInfo = useAtomValue(userInfoAtom);

	const classes = useStyles();

	const { data: currentUserData } = currentUser;

	const selectedOrganisation = useWizardFormField(savedOrganisation, { required: true });

	useEffect(() => {
		if (isSuperAdmin()) {
			save({
				selectedOrganisation: {
					value: selectedOrganisation.value,
					valid: selectedOrganisation.isValid,
				},
			});
		}
	}, [selectedOrganisation.value, selectedOrganisation.isValid]);

	useEffect(() => {
		if (!isSuperAdmin()) {
			save({
				selectedOrganisation: {
					valid: true,
					value: currentUserData.organisationReference,
				},
			});
		}
	}, [currentUserData]);

	const resetSelectedOrganisation = () => {
		selectedOrganisation.onChange({});
	};

	const name = useWizardFormField(savedName, { required: true, minLength: 3, maxLength: 25 });
	const checkedName = useDebouncedBackendFieldCheck(
		isSuperAdmin() ?
			!isEmptyObject(selectedOrganisation.value) ?
				name.value
			:	''
		:	name.value,
		onValidatePolicyName,
		validatedPolicyName,
		isSuperAdmin() ? { organisationId: selectedOrganisation.value.id } : {},
		true
	);
	const shouldValidateName =
		!editing || (editing && name.value.toLowerCase() !== currentPolicyName.toLowerCase());

	useDebouncedWizardSave(
		'name',
		name.value,
		name.isValid && (shouldValidateName ? checkedName.isUnique : true),
		save,
		300,
		name.isRequired
	);

	return (
		<Card className={className}>
			<CardHeader
				title={t('views.addPolicy.steps.details')}
				titleTypographyProps={{
					variant: 'h4',
				}}
			/>
			<CardContent>
				{(
					// TODO: statement doesn't make a lot of sense
					(isSuperAdmin() && editing) || (isSuperAdmin() && !editing)
				) ?
					<div className={classes.formGroup}>
						<Typography gutterBottom={true} variant='h5'>
							{t('views.addPolicy.aboutPolicy.subheader.selectOrganisation')}
							<sup>*</sup>
						</Typography>
						{!isEmptyObject(selectedOrganisation.value) ?
							<SelectedOrganisationCard
								handleClose={resetSelectedOrganisation}
								hasCloseButton={editing}
								name={selectedOrganisation.value.name}
							/>
						:	<SearchAutocomplete
								dataList={organisationsList}
								listType={'organisations'}
								onFetchData={onFetchOrganisations}
								placeholder={t('views.addPolicy.aboutPolicy.placeholder.searchOrganisation')}
								setSelected={selectedOrganisation.onChange}
							/>
						}
					</div>
				:	null}
				<div className={classes.formGroup}>
					<FormField
						extraValidHelperText={
							isSuperAdmin() && isEmptyObject(selectedOrganisation.value) ?
								t('views.addPolicy.error.nameAlreadyInSystem')
							:	t('views.addPolicy.error.nameAlreadyInUse')
						}
						isExtraValid={shouldValidateName ? checkedName.isUnique : true}
						label={t('ui.label.name')}
						maxLength={25}
						name='name'
						variable={name}
					/>
				</div>
				<Tip
					arrowDirection={'top'}
					message={t('views.policyManagement.actionFlows.aboutPolicy.tipDescription')}
				/>
			</CardContent>
		</Card>
	);
};

const mapStateToProps = (state) => {
	return {
		organisationsList: state.paged.organisations,
		validatedPolicyName: state.details.validatedPolicyName,

		currentUser: state.details.currentUser,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		onFetchOrganisations: (page, filters, concat) =>
			dispatch(actions.fetchOrganisations(page, filters, concat)),
		onValidatePolicyName: ({ value, organisationId }) =>
			dispatch(actions.validatePolicyName(value, organisationId)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(AboutPolicy);
